import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "lucide-react";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { customerActions, getCustomerDetails } from "../../utils/data";
import { useCallAction } from "../../utils/context/CallActionContext";
import { useSelectedCustomer } from "../../utils/context/SelectedCustomerContext";
import { db } from "../../utils/firebase/firebaseConfig";

const CustomerInformation = () => {
  const { selectedCustomerId, setSelectedCustomerId } = useSelectedCustomer();
  const { setShowSidebarActions } = useCallAction();
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch the first customer if none is selected (ordered by name)
  useEffect(() => {
    const fetchDefaultCustomer = async () => {
      try {
        const q = query(collection(db, "customers"), orderBy("name"));
        const querySnapshot = await getDocs(q);
        if (!selectedCustomerId && !querySnapshot.empty) {
          const firstDoc = querySnapshot.docs[0];
          setSelectedCustomerId(firstDoc.id);
        }
      } catch (error) {
        console.error("Error fetching default customer:", error);
      }
    };

    fetchDefaultCustomer();
  }, [selectedCustomerId, setSelectedCustomerId]);

  // Fetch selected customer info
  useEffect(() => {
    if (!selectedCustomerId) return;

    const fetchCustomer = async () => {
      setLoading(true);
      try {
        const ref = doc(db, "customers", selectedCustomerId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setCustomerInfo(snap.data());
        } else {
          setCustomerInfo(null);
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
        setCustomerInfo(null);
      }
      setLoading(false);
    };

    fetchCustomer();
  }, [selectedCustomerId]);

  if (loading) return <Loader />;
  if (!customerInfo)
    return (
      <div className="px-4 text-xs text-red-500">No customer data found.</div>
    );

  const customerDetails = getCustomerDetails(customerInfo);

  const handleActionClick = (idx) => {
    if (idx === 0) {
      setShowSidebarActions(true);
    } else if (idx === 1) {
      navigate("/chat");
    } else if (idx === 2) {
      navigate("/email-inbox");
    }
  };

  return (
    <div className="w-1/5 rounded-b-[12px] border-l border-[#0000002B] bg-white flex flex-col gap-2">
      <div className="w-full pl-2 h-[22px] border-b border-[#0000002B] flex items-start">
        <h1 className="h-[24px] text-[11px] lg:text-[13px] font-semibold leading-[100%] text-black font-inter">
          Customer Information
        </h1>
      </div>

      {/* Profile */}
      <div className="flex flex-row items-center px-2 w-full h-[60px]">
        <img
          src={customerInfo.profileImg}
          alt="Profile"
          className="w-[45px] h-[45px] rounded-full object-cover"
        />
        <div className="flex flex-col gap-y-1 ml-2 w-[123px] h-[40px] justify-center">
          <span className="text-[12px] font-semibold font-inter text-black leading-[100%]">
            {customerInfo.name}
          </span>
          <span className="text-[12px] font-medium font-inter text-black text-opacity-70 leading-[100%] tracking-[-0.03em]">
            {customerInfo.tier}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row items-start gap-3 px-2 w-[144px] h-[41px]">
        {customerActions.map((item, idx) => (
          <div
            key={idx}
            className="w-[28px] h-[28px] cursor-pointer rounded-[5px] shadow-[4px_4px_20px_0px_#00000040] bg-white flex items-center justify-center"
            onClick={() => handleActionClick(idx)}
          >
            <img src={item.icon} alt={item.alt} className="w-[18px] h-[18px]" />
          </div>
        ))}
      </div>

      {/* Customer Detail List */}
      <div className="flex flex-col w-full gap-4 px-2">
        {customerDetails.map((detail, idx) => (
          <div key={idx} className="flex flex-row items-start gap-2">
            <div className="w-[18px] h-[18px] flex items-center justify-center">
              <img
                src={detail.icon}
                alt={detail.label}
                className="w-[18px] h-[18px] object-contain"
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <span className="text-[12px] font-normal text-[#00000073] leading-[100%] font-inter">
                {detail.label}
              </span>
              <span className="text-[12px] font-medium text-[#000000CC] leading-[100%] font-inter tracking-[-0.03em]">
                {detail.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerInformation;
